// Handle generation and reservation service for InfluenceHub
// Provides unique handle generation with collision resolution

import { doc, getDoc, setDoc, serverTimestamp, runTransaction } from 'firebase/firestore';

/**
 * Normalizes a string to create a valid handle base
 * - Lowercases the string
 * - Removes diacritics and special characters
 * - Keeps only [a-z0-9]
 * - Converts spaces to nothing
 * - Trims and limits to 20 characters
 */
export function normalizeBase(str) {
  if (!str || typeof str !== 'string') return '';
  
  return str
    .toLowerCase()
    .normalize('NFD') // Decompose diacritics
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9\s]/g, '') // Keep only alphanumeric and spaces
    .replace(/\s+/g, '') // Remove spaces
    .trim()
    .substring(0, 20); // Limit to 20 characters
}

/**
 * Generates a random 4-character suffix for handle collision resolution
 */
function generateRandomSuffix() {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 4; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Reserves a unique handle in Firestore
 * Uses transactions to ensure atomicity and prevent race conditions
 * 
 * @param {Firestore} db - Firestore database instance
 * @param {string} desiredBase - The desired handle base (e.g., "derekthach")
 * @returns {Promise<string>} - The reserved unique handle
 */
export async function reserveUniqueHandle(db, desiredBase) {
  const base = normalizeBase(desiredBase);
  
  if (!base) {
    throw new Error('Invalid handle base provided');
  }

  // Try base, then base + number, then base + random suffix
  const candidates = [
    base,
    ...Array.from({ length: 20 }, (_, i) => `${base}${i + 1}`),
    `${base}${generateRandomSuffix()}`
  ];

  for (const candidate of candidates) {
    try {
      const result = await runTransaction(db, async (transaction) => {
        const handleRef = doc(db, 'usernames', candidate);
        const handleDoc = await transaction.get(handleRef);
        
        if (handleDoc.exists()) {
          // Handle already exists, throw error to try next candidate
          throw new Error('Handle exists');
        }
        
        // Reserve the handle with pending status
        transaction.set(handleRef, {
          uid: '__pending__',
          createdAt: serverTimestamp()
        });
        
        return candidate;
      });
      
      return result;
    } catch (error) {
      if (error.message === 'Handle exists') {
        // Try next candidate
        continue;
      }
      // Re-throw other errors
      throw error;
    }
  }
  
  throw new Error('Unable to generate unique handle after multiple attempts');
}

/**
 * Finalizes a handle reservation by updating it with the actual user UID
 * 
 * @param {Firestore} db - Firestore database instance
 * @param {string} handle - The handle to finalize
 * @param {string} uid - The user's UID
 */
export async function finalizeHandle(db, handle, uid) {
  const handleRef = doc(db, 'usernames', handle);
  
  await setDoc(handleRef, {
    uid,
    createdAt: serverTimestamp()
  }, { merge: true });
}

/**
 * Cleans up a pending handle reservation if user creation fails
 * 
 * @param {Firestore} db - Firestore database instance
 * @param {string} handle - The handle to clean up
 */
export async function cleanupPendingHandle(db, handle) {
  try {
    const handleRef = doc(db, 'usernames', handle);
    const handleDoc = await getDoc(handleRef);
    
    if (handleDoc.exists() && handleDoc.data().uid === '__pending__') {
      await setDoc(handleRef, {
        uid: null,
        createdAt: serverTimestamp()
      }, { merge: true });
    }
  } catch (error) {
    console.error('Error cleaning up pending handle:', error);
    // Don't throw - this is cleanup, not critical
  }
}
